import React, { FC, useRef, useState } from 'react'
import { BtGoogleAutocomplete, BtNumberInput } from '@linqpal/components/src/ui'
import { BtInput } from '@linqpal/components/src/ui/'
import { Col, Row } from '../../../../../ui/atoms/Grid'
import { useTranslation } from 'react-i18next'
import { StyleSheet } from 'react-native'
import { observer } from 'mobx-react'
import { useResponsive } from '@linqpal/components/src/hooks'

interface AddressEditorProps {
  model: {
    address: string
    city: string
    state: string
    zip: string
    setValue: (string, any) => void
  }
  collapsible?: boolean
  label?: string
  testID?: string
  review?: boolean
  onModelUpdate?: () => void
}

export const AddressEditor: FC<AddressEditorProps> = observer(
  ({
    model,
    collapsible = false,
    label = '',
    testID,
    review,
    onModelUpdate,
  }) => {
    const { t } = useTranslation('application')
    const { sm } = useResponsive()

    const shouldExpand = () =>
      !collapsible ||
      !!model.address ||
      !!model.state ||
      !!model.city ||
      !!model.zip

    const [expanded, setExpanded] = useState(shouldExpand)

    const autoCompleteRef = useRef<HTMLDivElement>(null)

    const onSelect = (value) => {
      model.setValue('address', value.address)
      model.setValue('state', value.state)
      model.setValue('city', value.city)
      model.setValue('zip', value.zip)

      setExpanded(true)
      !!onModelUpdate && onModelUpdate()
    }

    const onAddressFocused = () => {
      if (!sm) {
        autoCompleteRef?.current?.scrollIntoView()
      }
    }

    const onAddressBlurred = () => {
      if (!shouldExpand()) {
        setExpanded(false)
      }
    }

    const updateModel = (field?: string, value?: string) => {
      if (field) {
        model.setValue(field, value)
      }

      !!onModelUpdate && onModelUpdate()
    }

    const resetAddress = () => {
      model.setValue('address', '')
      model.setValue('state', '')
      model.setValue('city', '')
      model.setValue('zip', '')

      !!onModelUpdate && onModelUpdate()
    }

    const formatAddress = () => {
      return [model.address, model.city, model.state, model.zip]
        .filter((c) => !!c)
        .join(', ')
    }

    return (
      <>
        <Row>
          <Col xs={12}>
            <div
              ref={autoCompleteRef}
              style={{
                marginTop: review ? 24 : 4,
              }}
            >
              <BtGoogleAutocomplete
                value={expanded ? model.address : formatAddress()}
                onChange={(text) => {
                  if (!text) {
                    resetAddress()
                  } else if (expanded) {
                    updateModel('address', text)
                  }
                }}
                onSelect={onSelect}
                onFocus={onAddressFocused}
                onBlur={onAddressBlurred}
                label={
                  expanded
                    ? t('Business.StreetAddressLabel')
                    : label || t('Business.HomeAddressLabel')
                }
                testID={`${testID}.address`}
              />
            </div>
          </Col>
        </Row>
        {expanded && (
          <>
            <Row>
              <Col xs={12}>
                <BtNumberInput
                  value={model.zip}
                  onChangeText={(text) => updateModel('zip', text)}
                  style={styles.textBox}
                  label={t('Business.ZipLabel')}
                  testID={`${testID}.zip`}
                  format={'#####'}
                  disabled
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <BtInput
                  value={model.city}
                  onChangeText={(text) => updateModel('city', text)}
                  style={styles.textBox}
                  label={t('Business.CityLabel')}
                  testID={`${testID}.city`}
                  disabled
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <BtInput
                  value={model.state}
                  onChangeText={(text) => updateModel('state', text)}
                  style={styles.textBox}
                  label={t('Business.StateLabel')}
                  testID={`${testID}.state`}
                  disabled
                />
              </Col>
            </Row>
          </>
        )}
      </>
    )
  },
)

const styles = StyleSheet.create({
  textBox: { marginTop: 24 },
})
